import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { isIOS, isAndroid, isBrowser } from 'react-device-detect';
import {
  useMediaQuery,
  InputLabel,
  Select,
  MenuItem,
  FormControl,
  Button,
  TextField,
  Link,
  Snackbar,
  IconButton,
  Box,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import InputAdornment from '@mui/material/InputAdornment';
import TestDeviceIcon from '@mui/icons-material/TapAndPlay';
import { useTheme } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { sessionActions } from '../store';
import { useLocalization, useTranslation } from '../common/components/LocalizationProvider';
import LoginLayout from './LoginLayout';
import usePersistedState from '../common/util/usePersistedState';
import { handleErrorResponse } from '../common/util/utils';
import { handleLoginTokenListeners, nativeEnvironment, nativePostMessage } from '../common/components/NativeInterface';
import LogoImage from './LogoImage';
import { useCatch } from '../reactHelper';
import appStoreBadge from '../resources/images/app-store-badge.png';
import googlePlayBadge from '../resources/images/google-play-badge.png';

const useStyles = makeStyles((theme) => ({
  options: {
    // Pinned to the top-right corner of the viewport, so it must clear the
    // status bar and cutout itself; root padding does not reach it.
    position: 'absolute',
    top: `calc(${theme.spacing(2)} + var(--safe-area-inset-top))`,
    right: `calc(${theme.spacing(2)} + var(--safe-area-inset-right))`,
    zIndex: 10,
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  extraContainer: {
    display: 'flex',
    gap: theme.spacing(2),
  },
  registerButton: {
    minWidth: 'unset',
  },
  resetPassword: {
    cursor: 'pointer',
    textAlign: 'center',
    marginTop: theme.spacing(2),
  },
  badgeContainer: {
    display: 'flex',
    justifyContent: 'center',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  badge: {
    height: '40px',
  },
}));

function isWebView() {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;

  const isIOSWebView = /(iPhone|iPod|iPad).*AppleWebKit(?!.*Safari)/i.test(userAgent);
  const isAndroidWebView = /Android/.test(userAgent) && /wv/.test(userAgent);

  return isIOSWebView || isAndroidWebView;
}

const LoginPage = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const t = useTranslation();
  const [showBadges, setShowBadges] = useState(true);

  const [values, setValues] = useState({
    password: '',
    showPassword: false,
  });

  const handleClickShowPassword = () => {
    setValues({ ...values, showPassword: !values.showPassword });
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const { languages, language, setLanguage } = useLocalization();
  const languageList = Object.entries(languages).map((values) => ({ code: values[0], name: values[1].name }));

  const [failed, setFailed] = useState(false);

  const [email, setEmail] = usePersistedState('loginEmail', '');
  const [password, setPassword] = useState('');

  const registrationEnabled = useSelector((state) => state.session.server.registration);
  const languageEnabled = useSelector((state) => !state.session.server.attributes['ui.disableLoginLanguage']);
  const emailEnabled = useSelector((state) => state.session.server.emailEnabled);

  const [announcementShown, setAnnouncementShown] = useState(false);
  const announcement = useSelector((state) => state.session.server.announcement);

  const generateLoginToken = async () => {
    if (nativeEnvironment) {
      let token = '';
      try {
        const expiration = moment().add(6, 'months').toISOString();
        const response = await fetch('/api/session/token', {
          method: 'POST',
          body: new URLSearchParams(`expiration=${expiration}`),
        });
        if (response.ok) {
          token = await response.text();
        }
      } catch (error) {
        token = '';
      }
      nativePostMessage(`login|${token}`);
    }
  };

  const handlePasswordLogin = async (event) => {
    event.preventDefault();
    try {
      const response = await fetch('/api/session', {
        method: 'POST',
        body: new URLSearchParams(`email=${encodeURIComponent(email)}&password=${encodeURIComponent(password)}`),
      });
      if (response.ok) {
        const user = await response.json();
        generateLoginToken();
        dispatch(sessionActions.updateUser(user));
        navigate('/');
      } else {
        await handleErrorResponse(response);
      }
    } catch (error) {
      setFailed(true);
      setPassword('');
    }
  };

  const handleTokenLogin = useCatch(async (token) => {
    const response = await fetch(`/api/session?token=${encodeURIComponent(token)}`);
    if (response.ok) {
      const user = await response.json();
      dispatch(sessionActions.updateUser(user));
      navigate('/');
    } else {
      await handleErrorResponse(response);
    }
  });

  const handleSpecialKey = (e) => {
    if (e.keyCode === 13 && email && password) {
      handlePasswordLogin(e);
    }
  };

  useEffect(() => nativePostMessage('authentication'), []);

  useEffect(() => {
    const listener = (token) => handleTokenLogin(token);
    handleLoginTokenListeners.add(listener);
    return () => handleLoginTokenListeners.delete(listener);
  }, []);

  useEffect(() => {
    if (isWebView()) {
      setShowBadges(false);
    }
  }, []);

  // Navigate to the installation page
  const handleInstallationPage = () => {
    navigate('/install');
  };

  return (
    <LoginLayout>
      {/* Add the plug icon */}
      <IconButton
        onClick={handleInstallationPage}
        className={classes.options}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <TestDeviceIcon />
          <Box sx={{ fontSize: '0.875rem', mt: 0.5 }}>
            {t('sharedTestDevice')}
          </Box>
        </Box>
      </IconButton>
      <div className={classes.container}>
        {useMediaQuery(theme.breakpoints.down('lg')) && <LogoImage color={theme.palette.primary.main} />}
        <TextField
          required
          error={failed}
          label={t('userEmail')}
          name="email"
          value={email}
          autoComplete="email"
          autoFocus={!email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyUp={handleSpecialKey}
          helperText={failed && 'Invalid username or password'}
        />
        <TextField
          required
          error={failed}
          label={t('userPassword')}
          name="password"
          value={password}
          type={values.showPassword ? 'text' : 'password'}
          autoComplete="current-password"
          autoFocus={!!email}
          onChange={(e) => setPassword(e.target.value)}
          onKeyUp={handleSpecialKey}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={handleClickShowPassword}
                  onMouseDown={handleMouseDownPassword}
                >
                  {values.showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <Button
          onClick={handlePasswordLogin}
          onKeyUp={handleSpecialKey}
          variant="contained"
          color="secondary"
          disabled={!email || !password}
        >
          {t('loginLogin')}
        </Button>
        <div className={classes.extraContainer}>
          {registrationEnabled && (
            <Button
              className={classes.registerButton}
              onClick={() => navigate('/register')}
              color="secondary"
            >
              {t('loginRegister')}
            </Button>
          )}
          {languageEnabled && (
            <FormControl fullWidth>
              <InputLabel>{t('loginLanguage')}</InputLabel>
              <Select label={t('loginLanguage')} value={language} onChange={(e) => setLanguage(e.target.value)}>
                {languageList.map((it) => <MenuItem key={it.code} value={it.code}>{it.name}</MenuItem>)}
              </Select>
            </FormControl>
          )}
        </div>
        {emailEnabled && (
          <Link
            onClick={() => navigate('/reset-password')}
            className={classes.resetPassword}
            underline="none"
            variant="caption"
          >
            {t('loginReset')}
          </Link>
        )}
        <Link
          onClick={() => navigate('/terms-and-conditions/')}
          className={classes.resetPassword}
          underline="none"
          variant="caption"
        >
          {t('termsAndConditions')}
        </Link>
        {/* Badge logic */}
        {showBadges && isIOS && (
          <Box className={classes.badgeContainer}>
            <Link href="https://apps.apple.com/us/app/towergps/id6503110001" target="_blank">
              <img src={appStoreBadge} alt="Download on the App Store" className={classes.badge} />
            </Link>
          </Box>
        )}
        {showBadges && isAndroid && (
          <Box className={classes.badgeContainer}>
            <Link href="https://play.google.com/store/apps/details?id=com.towergps.manager" target="_blank">
              <img src={googlePlayBadge} alt="Get it on Google Play" className={classes.badge} />
            </Link>
          </Box>
        )}
        {showBadges && isBrowser && (
          <Box className={classes.badgeContainer}>
            <Link href="https://apps.apple.com/us/app/towergps/id6503110001" target="_blank">
              <img src={appStoreBadge} alt="Download on the App Store" className={classes.badge} />
            </Link>
            <Link href="https://play.google.com/store/apps/details?id=com.towergps.manager" target="_blank">
              <img src={googlePlayBadge} alt="Get it on Google Play" className={classes.badge} />
            </Link>
          </Box>
        )}
      </div>
      <Snackbar
        open={!!announcement && !announcementShown}
        message={announcement}
        action={(
          <IconButton size="small" color="inherit" onClick={() => setAnnouncementShown(true)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      />
    </LoginLayout>
  );
};

export default LoginPage;
